/**
 * Motion engine.
 *
 * Ported from the Claude Design comp (`Soorya Prakash Portfolio v2.dc.html`),
 * which shipped its behaviour as real code rather than a description. Timings,
 * easing curves and distances are kept as authored — they are the design.
 *
 * Effects, all driven by data attributes so the markup stays declarative:
 *   [data-load]      staggered entrance on first paint
 *   [data-split]     headings that reveal word by word from a mask
 *   [data-reveal]    fade + rise when scrolled into view
 *   [data-count]     numerals counting up from zero
 *   [data-magnetic]  buttons that lean toward the cursor
 *   [data-case]      project rows: colour wipe + number shift + custom cursor
 *   [data-par]       parallax at per-element rates
 *   [data-section]   sidebar theme inversion per section
 *
 * Everything degrades under `prefers-reduced-motion`: content is shown in its
 * final state rather than animated, and the pointer-driven effects never bind.
 */

const q = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const E = 'cubic-bezier(.22,1,.36,1)';
const E2 = 'cubic-bezier(.16,1,.3,1)';

/* ------------------------------------------------------------ word split */

/**
 * Wrap each word in an overflow-hidden mask so it can slide up from nothing.
 * Runs before any reveal so the observer has something to animate.
 */
function prepareSplit(reduced) {
  q('[data-split]').forEach((h) => {
    const words = h.textContent.trim().split(/\s+/);
    h.innerHTML = '';

    words.forEach((w, i) => {
      const mask = document.createElement('span');
      mask.style.cssText =
        'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:.08em;margin-right:.22em';

      const inner = document.createElement('span');
      inner.style.cssText =
        `display:inline-block;transform:translateY(${reduced ? 0 : 110}%);` +
        `opacity:${reduced ? 1 : 0};` +
        `transition:transform .76s ${E2} ${i * 55}ms,opacity .5s linear ${i * 55}ms`;

      // A trailing full stop is accented, matching the comp's "PRAKASH." mark.
      // Marked contrast-exempt: it is decorative punctuation carrying no
      // information, and the word it follows is already fully legible.
      if (w.endsWith('.') && w.length > 1) {
        inner.textContent = w.slice(0, -1);
        const dot = document.createElement('span');
        dot.textContent = '.';
        dot.style.color = 'var(--orange)';
        dot.setAttribute('data-contrast-exempt', '');
        dot.setAttribute('aria-hidden', 'true');
        inner.appendChild(dot);
      } else {
        inner.textContent = w;
      }

      mask.appendChild(inner);
      h.appendChild(mask);
    });

    h._words = q('span > span', h);
  });
}

/* ------------------------------------------------------------- count up */

const counted = new WeakSet();

function runCount(el, reduced) {
  if (counted.has(el)) return;
  counted.add(el);

  const to = parseFloat(el.dataset.count);
  const dec = (el.dataset.count.split('.')[1] || '').length;
  const pre = el.dataset.prefix || '';
  const suf = el.dataset.suffix || '';
  const fmt = (v) => pre + v.toFixed(dec) + suf;

  if (reduced) {
    el.textContent = fmt(to);
    return;
  }

  const t0 = performance.now();
  const dur = 1100;
  const step = (now) => {
    const p = Math.min((now - t0) / dur, 1);
    const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
    el.textContent = fmt(to * eased);
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/* -------------------------------------------------------------- reveals */

export function initMotion() {
  const reduced = reduce();

  prepareSplit(reduced);

  /*
   * ⚠️ Under reduced-motion, reveal targets must be left ALONE — not hidden and
   * then shown. Setting opacity:0 here relies on the observer to undo it, and
   * anything the observer misses stays invisible forever. That is exactly the
   * population least able to tolerate a blank page.
   */
  if (!reduced) {
    q('[data-reveal]').forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(36px)';
      el.style.transition = `opacity .7s ${E},transform .7s ${E}`;
    });
  } else {
    q('[data-reveal]').forEach((el) => {
      el.dataset.shown = '1';
    });
  }

  const show = (el) => {
    if (el.dataset.shown) return;
    el.dataset.shown = '1';
    el.style.opacity = '1';
    el.style.transform = 'none';
    q('[data-count]', el).forEach((c) => runCount(c, reduced));
  };

  const showWords = (h) => {
    if (h.dataset.shown) return;
    h.dataset.shown = '1';
    (h._words || []).forEach((w) => {
      w.style.transform = 'none';
      w.style.opacity = '1';
    });
  };

  const io = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry, i) => {
        if (!entry.isIntersecting) return;
        const t = entry.target;
        setTimeout(() => {
          if (t.hasAttribute('data-split')) showWords(t);
          else show(t);
        }, i * 80);
        io.unobserve(t);
      }),
    // Wide horizontal margin so the observer fires on vertical position alone.
    { threshold: 0, rootMargin: '0px 3000px -60px 3000px' }
  );
  q('[data-reveal],[data-split]').forEach((el) => io.observe(el));

  // Counters outside a reveal container need their own trigger.
  q('[data-count]').forEach((el) => {
    if (el.closest('[data-reveal]')) return;
    const o = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (!e.isIntersecting) return;
          runCount(el, reduced);
          o.unobserve(el);
        }),
      { threshold: 0, rootMargin: '0px 3000px 0px 3000px' }
    );
    o.observe(el);
  });

  /*
   * Safety sweep. Anything already scrolled past must never stay invisible —
   * this is the same class of bug that once left the whole contact section
   * blank. Also force-reveals everything once the document bottom is reached,
   * since each reveal grows the page and can outrun a scripted scroll.
   */
  let pending = false;
  const sweep = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      q('[data-reveal],[data-split]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (atBottom || (r.top < window.innerHeight - 40 && r.bottom > 0)) {
          el.hasAttribute('data-split') ? showWords(el) : show(el);
        }
      });
    });
  };
  window.addEventListener('scroll', sweep, { passive: true });
  window.addEventListener('resize', sweep, { passive: true });
  sweep();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(sweep);
  [200, 700, 1400].forEach((ms) => setTimeout(sweep, ms));

  /* ---------------------------------------------------- load sequence -- */

  if (reduced) {
    // Show everything in its final state and run counters at once.
    q('[data-load]').forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    q('[data-count]').forEach((el) => runCount(el, true));
  }

  q('[data-load]').forEach((el) => {
    const i = +el.dataset.load;
    const delay = 100 + i * 90;

    if (reduced) {
      el.style.opacity = '1';
      return;
    }

    const isLine = el.hasAttribute('data-line');
    el.style.opacity = '0';
    el.style.transform = isLine ? 'translateY(105%)' : 'translateY(16px)';
    el.style.transition =
      `opacity .6s linear ${delay}ms,` +
      `transform ${isLine ? `.82s ${E2}` : `.6s ${E}`} ${delay}ms`;

    // Double rAF so the initial state is painted before the transition starts.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      })
    );
  });

  if (reduced) return;

  /* ------------------------------------------- case rows + custom cursor */

  const cursor = document.querySelector('[data-cursor]');
  const target = { x: -200, y: -200 };
  const pos = { x: -200, y: -200 };

  q('[data-case]').forEach((row) => {
    const wipe = row.querySelector('[data-wipe]');
    const num = row.querySelector('[data-num]');

    row.addEventListener('mouseenter', () => {
      if (wipe) {
        wipe.style.transformOrigin = 'left';
        wipe.style.transform = 'scaleX(1)';
      }
      if (num) num.style.transform = 'translateX(-6px)';
      row.style.paddingLeft = '24px';
      row.style.paddingRight = '24px';
      if (cursor) {
        cursor.style.opacity = '1';
        cursor.style.transform = 'scale(1)';
      }
    });

    row.addEventListener('mouseleave', () => {
      if (wipe) {
        // Exit from the opposite edge so the wipe reads as a sweep, not a snap.
        wipe.style.transformOrigin = 'right';
        wipe.style.transform = 'scaleX(0)';
      }
      if (num) num.style.transform = 'none';
      row.style.paddingLeft = '0';
      row.style.paddingRight = '0';
      if (cursor) {
        cursor.style.opacity = '0';
        cursor.style.transform = 'scale(.4)';
      }
    });
  });

  window.addEventListener(
    'mousemove',
    (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
    },
    { passive: true }
  );

  /* --------------------------------------------------- magnetic buttons */

  q('[data-magnetic]').forEach((b) => {
    let raf = null;
    const t = { x: 0, y: 0 };
    const c = { x: 0, y: 0 };

    const loop = () => {
      c.x += (t.x - c.x) * 0.2;
      c.y += (t.y - c.y) * 0.2;
      b.style.translate = `${c.x}px ${c.y}px`;
      if (Math.abs(t.x - c.x) > 0.1 || Math.abs(t.y - c.y) > 0.1) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
      }
    };

    b.addEventListener('mousemove', (e) => {
      const r = b.getBoundingClientRect();
      t.x = (e.clientX - (r.left + r.width / 2)) * 0.28;
      t.y = (e.clientY - (r.top + r.height / 2)) * 0.34;
      if (!raf) raf = requestAnimationFrame(loop);
    });

    b.addEventListener('mouseleave', () => {
      t.x = 0;
      t.y = 0;
      if (!raf) raf = requestAnimationFrame(loop);
    });
  });

  /* ------------------------------------- parallax + cursor + inversion */

  const glow = document.querySelector('[data-glow]');
  const parallax = q('[data-par]');
  const sections = q('[data-section]');
  const sidebar = document.querySelector('[data-sidebar]');
  const progress = document.querySelector('[data-progress]');
  const indicator = document.querySelector('[data-indicator]');
  const navLinks = q('[data-navlink]');

  let raf = 0;
  const tick = () => {
    const y = window.scrollY;

    // Per-element parallax rates, as authored in the comp.
    parallax.forEach((el) => {
      const rate = parseFloat(el.dataset.par) || 0;
      el.style.transform = `translate3d(0,${(-y * rate) / 100}px,0)`;
    });

    // The glow drifts at a fraction of scroll so it feels attached to the page
    // rather than pinned to the window.
    if (glow) glow.style.transform = `translate3d(0,${-y * 0.06}px,0)`;

    if (progress) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
    }

    /*
     * Sidebar theme inversion. The sidebar is dark by default and flips to
     * cream while a section marked `data-theme="light"` owns the viewport —
     * the comp's signature moment.
     */
    if (sidebar) {
      let active = null;
      const line = window.innerHeight * 0.4;
      sections.forEach((s) => {
        const r = s.getBoundingClientRect();
        if (r.top <= line && r.bottom > line) active = s;
      });
      if (active) {
        const light = active.dataset.theme === 'light';
        sidebar.dataset.inverted = String(light);

        const id = active.dataset.section;
        navLinks.forEach((a) => {
          const on = a.dataset.navlink === id;
          a.dataset.active = String(on);
          if (on && indicator) {
            const ar = a.getBoundingClientRect();
            const nr = a.parentElement.getBoundingClientRect();
            indicator.style.opacity = '1';
            indicator.style.transform = `translateY(${ar.top - nr.top}px)`;
          }
        });
      }
    }

    if (cursor) {
      pos.x += (target.x - pos.x) * 0.16;
      pos.y += (target.y - pos.y) * 0.16;
      cursor.style.left = `${pos.x - 38}px`;
      cursor.style.top = `${pos.y - 38}px`;
    }

    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return {
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', sweep);
      window.removeEventListener('resize', sweep);
    },
  };
}
